import DataFileMap from '@/model/Resource/DataFileMap'

/** 在虚拟资源树中查找某节点的直接父节点。 */
export function findParent(root: DataFileMap, node: DataFileMap): DataFileMap | null {
  for (const c of root.children) {
    if (c === node) return root
    const p = findParent(c, node)
    if (p) return p
  }
  return null
}

/** ancestor 的子树中是否包含 node（含自身） */
export function isDescendant(ancestor: DataFileMap, node: DataFileMap): boolean {
  if (ancestor === node) return true
  for (const c of ancestor.children) {
    if (isDescendant(c, node)) return true
  }
  return false
}

/** 在同一父目录下生成唯一的新文件夹名称。 */
export function uniqueFolderName(parent: DataFileMap, base: string): string {
  const safe = base.trim() || '新建文件夹'
  const names = new Set(parent.children.filter((c) => c.isFolder).map((c) => c.name))
  if (!names.has(safe)) return safe
  let i = 2
  while (names.has(`${safe} (${i})`)) i += 1
  return `${safe} (${i})`
}

/**
 * 树内拖拽文件夹（与 naive-ui NTree onDrop 语义一致）
 * @param categoryRoot 当前分类虚拟根（不可被移动）
 */
export function applyVirtualFolderDrop(
  categoryRoot: DataFileMap,
  dragFolder: DataFileMap,
  dropFolder: DataFileMap,
  dropPosition: 'before' | 'after' | 'inside',
): boolean {
  if (dragFolder === categoryRoot) return false
  if (!dragFolder.isFolder || !dropFolder.isFolder) return false
  if (dragFolder === dropFolder) return false
  if (dropPosition === 'inside' && isDescendant(dragFolder, dropFolder)) return false

  const parentSrc = findParent(categoryRoot, dragFolder)
  if (!parentSrc) return false
  const srcIdx = parentSrc.children.indexOf(dragFolder)
  if (srcIdx < 0) return false

  parentSrc.children.splice(srcIdx, 1)

  if (dropPosition === 'inside') {
    dropFolder.children.push(dragFolder)
    return true
  }

  const parentDst = findParent(categoryRoot, dropFolder)
  if (!parentDst) {
    parentSrc.children.splice(srcIdx, 0, dragFolder)
    return false
  }
  const refIdx = parentDst.children.indexOf(dropFolder)
  if (refIdx < 0) {
    parentSrc.children.splice(srcIdx, 0, dragFolder)
    return false
  }
  const insertAt = dropPosition === 'before' ? refIdx : refIdx + 1
  parentDst.children.splice(insertAt, 0, dragFolder)
  return true
}

/** 从虚拟资源树中删除一个节点；根节点不可删除。 */
export function removeNodeFromTree(root: DataFileMap, node: DataFileMap): boolean {
  if (node === root) return false
  const p = findParent(root, node)
  if (!p) return false
  const i = p.children.indexOf(node)
  if (i < 0) return false
  p.children.splice(i, 1)
  return true
}

/** 将父节点 children 中一项移动到另一下标（用于同目录内排序） */
export function reorderChildAt(parent: DataFileMap, fromIndex: number, toIndex: number): void {
  const arr = parent.children
  if (fromIndex === toIndex) return
  if (fromIndex < 0 || fromIndex >= arr.length) return
  if (toIndex < 0 || toIndex > arr.length) return
  const [it] = arr.splice(fromIndex, 1)
  let ins = toIndex
  if (fromIndex < toIndex) ins = toIndex - 1
  arr.splice(ins, 0, it)
}

/** 在两个目录之间移动一个文件叶子节点，可选指定插入位置。 */
export function moveFileChildToFolder(
  categoryRoot: DataFileMap,
  sourceFolder: DataFileMap,
  childIndex: number,
  targetFolder: DataFileMap,
  insertBeforeIndex?: number,
): boolean {
  const leaf = sourceFolder.children[childIndex]
  if (!leaf || leaf.isFolder) return false
  if (!isDescendant(categoryRoot, sourceFolder) || !isDescendant(categoryRoot, targetFolder)) return false

  sourceFolder.children.splice(childIndex, 1)

  let ins: number
  if (insertBeforeIndex === undefined || insertBeforeIndex > targetFolder.children.length) {
    ins = targetFolder.children.length
  } else {
    ins = insertBeforeIndex
    if (sourceFolder === targetFolder && childIndex < insertBeforeIndex) {
      ins = insertBeforeIndex - 1
    }
  }
  if (ins < 0) ins = 0
  if (ins > targetFolder.children.length) ins = targetFolder.children.length
  targetFolder.children.splice(ins, 0, leaf)
  return true
}
